import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, Send, User, Users, Briefcase, Clock,
  CheckCircle, Circle, ArrowRight, Sparkles
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

const channels = [
  { 
    id: 'rh', 
    name: 'Responsable RH', 
    icon: Users,
    description: 'Recrutement de ton assistante',
    color: 'from-purple-500 to-purple-600',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-600'
  },
  { 
    id: 'projet', 
    name: 'Chef de Projet', 
    icon: Briefcase,
    description: 'Paramétrage CRM & Agents IA',
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-600'
  }
];

export default function ClientTeamChatPage() {
  const [activeChannel, setActiveChannel] = useState('rh');
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    fetchAccountStatus();
  }, []);

  useEffect(() => {
    if (activeChannel) {
      fetchMessages(activeChannel);
    }
  }, [activeChannel]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/client-chat/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setConversations(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchMessages = async (channel) => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/client-chat/messages/${channel}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setMessages(await response.json());
        // Refresh conversations to update unread counts
        fetchConversations();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountStatus = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/client-chat/account-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setAccountStatus(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    const token = localStorage.getItem('client_token');
    if (!token) return;

    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/client-chat/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: newMessage,
          channel: activeChannel
        })
      });

      if (response.ok) {
        setNewMessage('');
        fetchMessages(activeChannel);
      } else {
        toast.error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const getUnreadCount = (channelId) => {
    const conv = conversations.find(c => c.channel === channelId);
    return conv?.unread_count_client || 0;
  };

  const activeChannelInfo = channels.find(c => c.id === activeChannel);

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="client-team-chat-page">
      {/* Onboarding Status Banner */}
      {accountStatus?.account_status === 'onboarding' && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5" />
            <div>
              <p className="font-semibold">Phase de mise en place</p>
              <p className="text-sm text-white/90">
                {accountStatus.onboarding_days_remaining > 0 
                  ? `${accountStatus.onboarding_days_remaining} jours restants pour paramétrer ton espace et recruter ton assistante`
                  : 'Ton assistante sera bientôt disponible !'
                }
              </p>
            </div>
          </div>
          <Sparkles className="w-6 h-6" />
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Channels Sidebar */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-bold text-gray-900">Ton équipe dédiée</h2>
            <p className="text-sm text-gray-500">Échangez avec l'équipe</p>
          </div>
          
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {channels.map(channel => {
              const unread = getUnreadCount(channel.id);
              const conv = conversations.find(c => c.channel === channel.id);
              const Icon = channel.icon;
              
              return (
                <button
                  key={channel.id}
                  onClick={() => setActiveChannel(channel.id)}
                  className={`w-full p-4 rounded-xl text-left transition-all ${
                    activeChannel === channel.id
                      ? 'bg-gradient-to-r ' + channel.color + ' text-white shadow-lg'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                  data-testid={`channel-${channel.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      activeChannel === channel.id ? 'bg-white/20' : channel.bgColor
                    }`}>
                      <Icon className={`w-5 h-5 ${
                        activeChannel === channel.id ? 'text-white' : channel.textColor
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className={`font-semibold ${
                          activeChannel === channel.id ? 'text-white' : 'text-gray-900'
                        }`}>
                          {channel.name}
                        </h3>
                        {unread > 0 && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                            activeChannel === channel.id 
                              ? 'bg-white text-purple-600' 
                              : 'bg-red-500 text-white'
                          }`}>
                            {unread}
                          </span>
                        )}
                      </div>
                      <p className={`text-xs mt-0.5 ${
                        activeChannel === channel.id ? 'text-white/80' : 'text-gray-500'
                      }`}>
                        {channel.description}
                      </p>
                      {conv?.last_message && (
                        <p className={`text-sm mt-2 truncate ${
                          activeChannel === channel.id ? 'text-white/70' : 'text-gray-400'
                        }`}>
                          {conv.last_message}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col bg-gray-50">
          {/* Chat Header */}
          <div className={`bg-gradient-to-r ${activeChannelInfo?.color} text-white px-6 py-4`}>
            <div className="flex items-center gap-3">
              {activeChannelInfo && <activeChannelInfo.icon className="w-6 h-6" />}
              <div>
                <h2 className="font-bold text-lg">{activeChannelInfo?.name}</h2>
                <p className="text-sm text-white/80">{activeChannelInfo?.description}</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucun message pour le moment</p>
                <p className="text-sm">Envoyez un message pour démarrer la conversation</p>
              </div>
            ) : (
              messages.map((message, index) => {
                const isClient = message.sender_type === 'client';
                const showDate = index === 0 || 
                  new Date(message.created_at).toDateString() !== 
                  new Date(messages[index - 1].created_at).toDateString();
                
                return (
                  <div key={message.id}>
                    {showDate && (
                      <div className="text-center my-4">
                        <span className="bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full">
                          {new Date(message.created_at).toLocaleDateString('fr-FR', {
                            weekday: 'long', day: 'numeric', month: 'long'
                          })}
                        </span>
                      </div>
                    )}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${isClient ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] ${isClient ? 'order-2' : ''}`}>
                        {!isClient && (
                          <div className="flex items-center gap-2 mb-1">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                              message.sender_role === 'responsable_rh' ? 'bg-purple-100' : 'bg-blue-100'
                            }`}>
                              {message.sender_role === 'responsable_rh' 
                                ? <Users className="w-3 h-3 text-purple-600" />
                                : <Briefcase className="w-3 h-3 text-blue-600" />
                              }
                            </div>
                            <span className="text-xs font-medium text-gray-600">{message.sender_name}</span>
                          </div>
                        )}
                        <div className={`rounded-2xl px-4 py-3 ${
                          isClient
                            ? 'bg-blue-600 text-white rounded-br-md'
                            : 'bg-white text-gray-800 rounded-bl-md shadow-sm'
                        }`}>
                          <p className="whitespace-pre-wrap">{message.content}</p>
                        </div>
                        <div className={`flex items-center gap-1 mt-1 text-xs text-gray-400 ${
                          isClient ? 'justify-end' : ''
                        }`}>
                          <span>{formatTime(message.created_at)}</span>
                          {isClient && message.is_read && (
                            <CheckCircle className="w-3 h-3 text-blue-400" />
                          )}
                        </div>
                      </div>
                    </motion.div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <form onSubmit={handleSendMessage} className="p-4 bg-white border-t border-gray-200">
            <div className="flex gap-3">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={`Message au ${activeChannelInfo?.name}...`}
                className="flex-1 px-4 py-3 bg-gray-100 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors"
                disabled={sending}
                data-testid="chat-input"
              />
              <Button 
                type="submit" 
                disabled={!newMessage.trim() || sending}
                className={`px-6 bg-gradient-to-r ${activeChannelInfo?.color} hover:opacity-90`}
                data-testid="send-message-btn"
              >
                <Send className="w-5 h-5" />
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
